// /js/pages/catalog.js
// Catalog page controller (MPA — runs on page load, no initX wrapper).

import { supabase } from '/js/services/supabaseClient.js';
import { showToast }  from '/js/modules/toast.js';

console.log('📌 catalog.js loaded');

// ============================================
// STATE
// ============================================
let allBooks      = [];
let filteredBooks = [];
let currentView   = 'grid';

// ============================================
// BOOT
// ============================================
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCatalog);
} else {
    initCatalog();
}

async function initCatalog() {
    console.log('📚 Catalog page initializing');
    await loadBooks();
    setupEventListeners();
    wireViewToggle();
    wireClearFilters();
}

// ============================================
// LOAD BOOKS FROM SUPABASE
// ============================================
async function loadBooks() {
    const container = document.getElementById('booksContainer');
    if (!container) return;

    try {
        console.log('📖 Loading books from Supabase...');

        const { data, error } = await supabase
            .from('books')
            .select('*')
            .eq('is_active', true)
            .order('created_at', { ascending: false });

        if (error) throw error;

        if (data && data.length > 0) {
            allBooks      = data.map(normalizeBook);
            filteredBooks = allBooks;

            updateBookCount();
            renderBooks();
            updatePagination();
            populateCategories();
        } else {
            console.log('📚 No books in Supabase — showing empty state');
            renderEmptyState();
        }
    } catch (error) {
        console.error('❌ Error loading books:', error);
        showToast('Error loading books: ' + error.message, 'error');
        renderErrorState(error.message);
    }
}

// ============================================
// NORMALIZE SUPABASE SNAKE_CASE → CAMELCASE
// ============================================
function normalizeBook(book) {
    const currency = book.currency || 'NGN';
    const symbols  = { NGN: '₦', USD: '$', GHS: '₵', EUR: '€', GBP: '£' };
    const symbol   = symbols[currency] || '₦';

    return {
        id: book.id,
        title: book.title,
        author: book.author,
        description: book.description || '',
        categories: book.categories || [],
        price: book.price || 0,
        currency,
        formattedPrice: `${symbol}${Number(book.price || 0).toLocaleString()}`,

        isFeatured: book.is_featured || false,
        isSubscriptionOnly: book.is_subscription_only || false,
        isActive: book.is_active !== false,
        pageCount: book.page_count || 0,
        language: book.language || 'en',

        coverUrl: book.cover_url || '/assets/images/placeholder-book.svg',
        coverThumbnailUrl: book.cover_url || '/assets/images/placeholder-book.svg',

        fileUrl: book.file_url || null,
        fileType: book.file_type || null,
        epubUrl: book.epub_url || null,

        downloadCount: book.download_count || 0,
        viewCount: book.view_count || 0,
        createdAt: book.created_at,
        updatedAt: book.updated_at,
    };
}

// ============================================
// RENDER
// ============================================
function renderBooks() {
    const container = document.getElementById('booksContainer');
    const noResults = document.getElementById('noResults');
    if (!container) return;

    const isGridView = currentView === 'grid';

    if (!filteredBooks || filteredBooks.length === 0) {
        if (noResults) noResults.classList.remove('hidden');
        container.innerHTML = '';
        container.classList.add('hidden');
        updateBookCount();
        return;
    }

    if (noResults) noResults.classList.add('hidden');
    container.classList.remove('hidden');
    container.className = isGridView ? 'grid-books' : 'space-y-4';

    container.innerHTML = filteredBooks
        .map(book => isGridView ? createBookCard(book) : createBookListItem(book))
        .join('');

    updateBookCount();
}

function createBookCard(book) {
    const coverUrl = book.coverUrl || '/assets/images/placeholder-book.svg';
    const price    = book.formattedPrice;
    const href     = `/book-details?id=${encodeURIComponent(book.id)}`;

    let badges = '';
    if (book.isFeatured)         badges += '<span class="book-card-badge">Featured</span>';
    if (book.isSubscriptionOnly) badges += '<span class="book-card-badge subscriber">Subscriber</span>';

    return `
        <a class="book-card" href="${href}" style="text-decoration:none; color:inherit;">
            <div class="book-card-cover">
                <img src="${escapeAttr(coverUrl)}"
                     alt="${escapeAttr(book.title)}"
                     onerror="this.src='/assets/images/placeholder-book.svg'"
                     loading="lazy">
                ${badges}
            </div>
            <div class="book-card-body">
                <h3 class="book-card-title">${escapeHtml(book.title)}</h3>
                <p class="book-card-author">${escapeHtml(book.author || '')}</p>
                <div class="book-card-price">${price}</div>
            </div>
        </a>
    `;
}

function createBookListItem(book) {
    const coverUrl = book.coverUrl || '/assets/images/placeholder-book.svg';
    const price    = book.formattedPrice;
    const href     = `/book-details?id=${encodeURIComponent(book.id)}`;

    let badges = '';
    if (book.isFeatured)
        badges += `<span class="text-xs text-white px-2 py-1 rounded-full" style="background:#C41E24;">Featured</span>`;
    if (book.isSubscriptionOnly)
        badges += `<span class="text-xs text-white px-2 py-1 rounded-full ml-1" style="background:#1A2A5E;">Subscriber</span>`;

    const desc = book.description
        ? `<p class="text-sm text-gray-500 mt-1 line-clamp-2">${escapeHtml(book.description.substring(0, 150))}${book.description.length > 150 ? '…' : ''}</p>`
        : '';

    return `
        <a class="card p-4 hover:shadow-lg transition" href="${href}"
           style="text-decoration:none; color:inherit; display:block;">
            <div class="flex gap-4">
                <div class="w-24 h-32 flex-shrink-0">
                    <img src="${escapeAttr(coverUrl)}"
                         alt="${escapeAttr(book.title)}"
                         class="w-full h-full object-cover rounded-lg"
                         onerror="this.src='/assets/images/placeholder-book.svg'"
                         loading="lazy">
                </div>
                <div class="flex-1">
                    <h3 class="text-lg font-bold" style="color:#1A2A5E;">${escapeHtml(book.title)}</h3>
                    <p class="text-sm text-gray-600">by ${escapeHtml(book.author || '')}</p>
                    ${desc}
                    <div class="flex items-center gap-4 mt-2">
                        <span class="text-xl font-bold" style="color:#C41E24;">${price}</span>
                        ${badges}
                    </div>
                </div>
            </div>
        </a>
    `;
}

function renderEmptyState() {
    const container = document.getElementById('booksContainer');
    if (!container) return;
    container.classList.remove('grid-books');
    container.innerHTML = `
        <div class="text-center py-12 text-gray-500">
            <div class="text-6xl mb-4">📚</div>
            <p class="text-lg">No books available yet. Check back soon!</p>
            <p class="text-sm text-gray-400 mt-2">The library is being set up.</p>
            <a href="/" class="btn btn-primary mt-4">
                <i class="fas fa-home"></i> Back to Home
            </a>
        </div>`;
    updateBookCount();
}

function renderErrorState(msg) {
    const container = document.getElementById('booksContainer');
    if (!container) return;
    container.classList.remove('grid-books');
    container.innerHTML = `
        <div class="text-center py-12 text-red-500">
            <div class="text-6xl mb-4">⚠️</div>
            <p>Error loading books: ${escapeHtml(msg)}</p>
            <button id="retryBtn" class="btn btn-primary mt-4">
                <i class="fas fa-sync"></i> Retry
            </button>
        </div>`;
    document.getElementById('retryBtn')?.addEventListener('click', loadBooks);
}

function updateBookCount() {
    const el = document.getElementById('bookCount');
    if (!el) return;
    el.textContent = `${filteredBooks.length} ${filteredBooks.length === 1 ? 'book' : 'books'}`;
}

// ============================================
// PAGINATION (single page — placeholder)
// ============================================
function updatePagination() {
    const total = filteredBooks.length;
    const $ = (id) => document.getElementById(id);

    if ($('showingStart')) $('showingStart').textContent = total > 0 ? 1 : 0;
    if ($('showingEnd'))   $('showingEnd').textContent   = total;
    if ($('totalBooks'))   $('totalBooks').textContent   = total;
    if ($('pageInfo'))     $('pageInfo').textContent     = 'Page 1 of 1';
    if ($('prevPage'))     $('prevPage').disabled        = true;
    if ($('nextPage'))     $('nextPage').disabled        = true;
}

// ============================================
// FILTER + SORT EVENTS
// ============================================
function setupEventListeners() {
    // Search with debounce
    const searchInput = document.getElementById('searchInput');
    let searchTimeout;
    if (searchInput) {
        searchInput.addEventListener('input', (e) => {
            clearTimeout(searchTimeout);
            searchTimeout = setTimeout(() => {
                applyFilters({ search: e.target.value.trim().toLowerCase() });
            }, 300);
        });
    }

    const categoryFilter = document.getElementById('categoryFilter');
    if (categoryFilter) {
        categoryFilter.addEventListener('change', (e) => {
            applyFilters({ category: e.target.value });
        });
    }

    const sortFilter = document.getElementById('sortFilter');
    if (sortFilter) {
        sortFilter.addEventListener('change', (e) => {
            applyFilters({ sortBy: e.target.value });
        });
    }
}

let activeFilters = { search: '', category: '', sortBy: 'newest' };

function applyFilters(patch) {
    activeFilters = { ...activeFilters, ...patch };
    let result = [...allBooks];

    if (activeFilters.search) {
        const q = activeFilters.search;
        result = result.filter(b =>
            b.title.toLowerCase().includes(q) ||
            b.author.toLowerCase().includes(q) ||
            (b.description && b.description.toLowerCase().includes(q))
        );
    }

    if (activeFilters.category) {
        result = result.filter(b =>
            Array.isArray(b.categories) && b.categories.includes(activeFilters.category)
        );
    }

    switch (activeFilters.sortBy) {
        case 'price_low':  result.sort((a, b) => (a.price || 0) - (b.price || 0)); break;
        case 'price_high': result.sort((a, b) => (b.price || 0) - (a.price || 0)); break;
        case 'title_asc':  result.sort((a, b) => (a.title || '').localeCompare(b.title || '')); break;
        case 'title_desc': result.sort((a, b) => (b.title || '').localeCompare(a.title || '')); break;
        case 'oldest':     result.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0)); break;
        case 'newest':
        default:           result.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)); break;
    }

    filteredBooks = result;
    renderBooks();
    updatePagination();
}

// ============================================
// VIEW TOGGLE + CLEAR
// ============================================
function wireViewToggle() {
    document.getElementById('gridViewBtn')?.addEventListener('click', () => setView('grid'));
    document.getElementById('listViewBtn')?.addEventListener('click', () => setView('list'));
}

function setView(view) {
    currentView = view;
    document.getElementById('gridViewBtn')?.classList.toggle('active', view === 'grid');
    document.getElementById('listViewBtn')?.classList.toggle('active', view === 'list');
    renderBooks();
}

function wireClearFilters() {
    document.getElementById('clearFiltersBtn')?.addEventListener('click', clearFilters);
}

function clearFilters() {
    const s = document.getElementById('searchInput');
    const c = document.getElementById('categoryFilter');
    const o = document.getElementById('sortFilter');
    if (s) s.value = '';
    if (c) c.value = '';
    if (o) o.value = 'newest';

    activeFilters = { search: '', category: '', sortBy: 'newest' };
    filteredBooks = [...allBooks];
    renderBooks();
    updatePagination();
    showToast('Filters cleared', 'info');
}

// ============================================
// POPULATE CATEGORY DROPDOWN (from loaded books)
// ============================================
function populateCategories() {
    const select = document.getElementById('categoryFilter');
    if (!select) return;

    const counts = {};
    for (const b of allBooks) {
        if (Array.isArray(b.categories)) {
            for (const c of b.categories) {
                counts[c] = (counts[c] || 0) + 1;
            }
        }
    }

    const names = Object.keys(counts).sort();
    select.innerHTML = '<option value="">All Categories</option>' +
        names.map(n => `<option value="${escapeAttr(n)}">${escapeHtml(n)} (${counts[n]})</option>`).join('');
}

// ============================================
// HELPERS
// ============================================
function escapeHtml(s) {
    return String(s ?? '').replace(/[&<>"']/g, c => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
}
function escapeAttr(s) { return escapeHtml(s); }